import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  AddAPhoto as AddAPhotoIcon,
  Add as AddIcon,
  CheckCircleRounded as ActiveIcon,
  Close as CloseIcon,
  EcoRounded as EcoIcon,
  EmojiNatureRounded as WildIcon,
  FlagRounded as FrenchIcon,
  RoomRounded as LocalIcon,
  SpaRounded as OrganicIcon,
  StarRounded as TopIcon,
} from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import { useCreateBasket } from '../../hooks/create-basket.hook';
import { useUploadBasketImage } from '../../hooks/upload-basket-image.hook';
import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { useAdminGetTaxes } from '../../../taxes/hooks/admin-get-taxes.hook';
import { useAdminGetTags } from '../../../tags/hooks/admin-get-tags.hook';
import { useAdminGetWeightUnities } from '../../../weight-unities/hooks/admin-get-weight-unities.hook';
import { useAdminGetProducts } from '../../../products/hooks/admin-get-products.hook';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { Tax } from '../../../taxes/types/tax.type';
import { Tag } from '../../../tags/types/tag.type';
import { WeightUnity } from '../../../weight-unities/types/weight-unity.type';
import { Product } from '../../../products/types/product.type';
import { BASKET_SIZES, BasketSize } from '../../types/basket-size.type';
import { CreateBasketRequestDTO } from '../../types/dtos/create-basket.dto';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import RichTextEditor from '../../../shared/components/Forms/RichTextEditor.component';

interface CreateBasketDialogProps {
  open: boolean;
  onClose: () => void;
  onBasketCreated: () => void;
}

interface CreateBasketFormValues {
  name: string;
  reference: string;
  description: string;
  price: string;
  stock: string;
  category: Category | null;
  subCategory: SubCategory | null;
  tax: Tax | null;
  weightFrom: string;
  weightTo: string;
  weightUnity: WeightUnity | null;
  personsFrom: string;
  personsTo: string;
  availabilityFrom: string;
  availabilityTo: string;
  size: BasketSize;
  tags: Tag[];
}

const FORM_DEFAULT_VALUES: CreateBasketFormValues = {
  name: '',
  reference: '',
  description: '',
  price: '',
  stock: '100',
  category: null,
  subCategory: null,
  tax: null,
  weightFrom: '',
  weightTo: '',
  weightUnity: null,
  personsFrom: '',
  personsTo: '',
  availabilityFrom: '',
  availabilityTo: '',
  size: BASKET_SIZES.SOLO,
  tags: [],
};

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    paddingBottom: 0,
  },
  stepper: {
    padding: '0.5rem 4rem 1rem 4rem',
    margin: '0 auto',
    '& .MuiStepIcon-root': {
      borderRadius: 4,
    },
    '& .MuiStepIcon-root.MuiStepIcon-active': {
      color: theme.palette.admin.primary.main,
    },
    '& .MuiStepIcon-root.MuiStepIcon-completed': {
      color: theme.palette.admin.primary.main,
    },
    '& .MuiStepLabel-label': {
      fontSize: 13,
    },
    '& .MuiStepConnector-root': {
      borderColor: '#ccc',
      width: 100,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  topSection: {
    display: 'flex',
    gap: '1rem',
  },
  imagePlaceholder: {
    minWidth: 190,
    width: 190,
    height: 190,
    border: '2px dashed #ccc',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    backgroundColor: '#fafafa',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  imageIcon: {
    fontSize: 40,
    color: '#bbb',
  },
  fieldsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flex: 1,
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  chip: {
    cursor: 'pointer',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,

    '& .MuiChip-icon': {
      fontSize: 14,
      marginLeft: 6,
      marginRight: -4,
    },
  },
  chipSelected: {
    border: '1px solid transparent',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,

    '& .MuiChip-icon': {
      color: theme.palette.admin.primary.contrastText,
    },

    '&:hover, &:focus': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  chipDivider: {
    width: 1,
    height: 20,
    backgroundColor: '#ccc',
    marginLeft: '0.25rem',
    marginRight: '0.25rem',
  },
  step2Container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  step2Filters: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  productTableContainer: {
    maxHeight: 250,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  selectedSection: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '0.75rem',
  },
  selectedHeader: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: '0.5rem',
  },
  selectedList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  selectedChip: {
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '& .MuiChip-deleteIcon': {
      color: theme.palette.admin.primary.contrastText,
      opacity: 0.7,
      '&:hover': {
        opacity: 1,
      },
    },
  },
  actions: {
    padding: '8px 24px 24px 24px',

    '& .MuiButton-root': {
      textTransform: 'none',
    },
  },
  primaryButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

function CreateBasketDialog({ open, onClose, onBasketCreated }: CreateBasketDialogProps) {
  const classes = useStyles();
  const { createBasket, isSubmitting: isCreating } = useCreateBasket();
  const { uploadBasketImage, isSubmitting: isUploading } = useUploadBasketImage();
  const { categories } = useAdminGetCategories();
  const { taxes } = useAdminGetTaxes();
  const { tags } = useAdminGetTags();
  const { weightUnities } = useAdminGetWeightUnities();

  const [activeStep, setActiveStep] = useState(0);

  const { control, handleSubmit, watch, setValue, getValues, trigger, reset } = useForm<CreateBasketFormValues>({
    defaultValues: FORM_DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const selectedCategory = watch('category');
  const { subCategories } = useAdminGetSubCategories({ categoryId: selectedCategory?.id });

  const [organic, setOrganic] = useState(false);
  const [wild, setWild] = useState(false);
  const [french, setFrench] = useState(false);
  const [native, setNative] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isTop, setIsTop] = useState(false);
  const [isEco, setIsEco] = useState(false);

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState<Category | null>(null);
  const [productSubCategoryFilter, setProductSubCategoryFilter] = useState<SubCategory | null>(null);
  const [productIsActiveFilter, setProductIsActiveFilter] = useState('active');
  const debouncedProductSearch = useDebounce(productSearch, 400);

  const { subCategories: productSubCategories } = useAdminGetSubCategories({ categoryId: productCategoryFilter?.id });
  const { products } = useAdminGetProducts({
    page: 1,
    limit: 50,
    sortBy: 'name',
    sortDirection: 'asc',
    filters: {
      name: debouncedProductSearch || undefined,
      category: productCategoryFilter?.id || undefined,
      subCategory: productSubCategoryFilter?.id || undefined,
      isActive: productIsActiveFilter === 'all' ? undefined : productIsActiveFilter === 'active',
    },
  });

  const selectedProductIds = new Set(selectedProducts.map((product) => product.id));
  const availableProducts = products.filter((product) => !selectedProductIds.has(product.id));

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setSelectedFile(file);
    }
  };

  const addProduct = (product: Product) => {
    setSelectedProducts((previous) => [...previous, product]);
  };

  const removeProduct = (productId: string) => {
    setSelectedProducts((previous) => previous.filter((product) => product.id !== productId));
  };

  const resetForm = () => {
    setActiveStep(0);
    reset(FORM_DEFAULT_VALUES);
    setOrganic(false);
    setWild(false);
    setFrench(false);
    setNative(false);
    setIsActive(true);
    setIsTop(false);
    setIsEco(false);
    setImagePreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setSelectedProducts([]);
    setProductSearch('');
    setProductCategoryFilter(null);
    setProductSubCategoryFilter(null);
    setProductIsActiveFilter('active');
  };

  const handleNextStep = async () => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveStep(1);
  };

  const onSubmit = async (formValues: CreateBasketFormValues) => {
    if (!formValues.category || !formValues.subCategory || !formValues.tax || !formValues.weightUnity) return;

    const payload: CreateBasketRequestDTO = {
      name: formValues.name,
      reference: formValues.reference,
      description: formValues.description,
      photo: '',
      subCategory: formValues.subCategory.id,
      price: Number(formValues.price),
      taxId: formValues.tax.id,
      size: formValues.size,
      weight: {
        from: Number(formValues.weightFrom),
        to: Number(formValues.weightTo),
        unityId: formValues.weightUnity.id,
      },
      persons: { from: Number(formValues.personsFrom), to: Number(formValues.personsTo) },
      availability: { from: formValues.availabilityFrom, to: formValues.availabilityTo },
      labels: { organic, wild, french, native },
      stock: Number(formValues.stock),
      isActive,
      isTop,
      isEco,
    };

    if (formValues.tags.length > 0) payload.tags = formValues.tags.map((tag) => tag.id);
    if (selectedProducts.length > 0) payload.productIds = selectedProducts.map((product) => product.id);

    const createdBasket = await createBasket(payload);
    if (!createdBasket) return;

    if (selectedFile) {
      await uploadBasketImage(createdBasket.id, selectedFile);
    }

    onBasketCreated();
    onClose();
    resetForm();
  };

  const isSubmitting = isCreating || isUploading;

  const renderStep1 = () => (
    <div className={classes.content}>
      <div className={classes.chipRow}>
        <Chip
          className={clsx(classes.chip, isActive && classes.chipSelected)}
          label="Actif"
          icon={<ActiveIcon />}
          size="small"
          variant={isActive ? 'default' : 'outlined'}
          onClick={() => setIsActive((previous) => !previous)}
        />
        <div className={classes.chipDivider} />
        <Chip
          className={clsx(classes.chip, organic && classes.chipSelected)}
          label="Bio"
          icon={<OrganicIcon />}
          size="small"
          variant={organic ? 'default' : 'outlined'}
          onClick={() => setOrganic((previous) => !previous)}
        />
        <Chip
          className={clsx(classes.chip, wild && classes.chipSelected)}
          label="Sauvage"
          icon={<WildIcon />}
          size="small"
          variant={wild ? 'default' : 'outlined'}
          onClick={() => setWild((previous) => !previous)}
        />
        <Chip
          className={clsx(classes.chip, french && classes.chipSelected)}
          label="Français"
          icon={<FrenchIcon />}
          size="small"
          variant={french ? 'default' : 'outlined'}
          onClick={() => setFrench((previous) => !previous)}
        />
        <Chip
          className={clsx(classes.chip, native && classes.chipSelected)}
          label="Local"
          icon={<LocalIcon />}
          size="small"
          variant={native ? 'default' : 'outlined'}
          onClick={() => setNative((previous) => !previous)}
        />
        <div className={classes.chipDivider} />
        <Chip
          className={clsx(classes.chip, isTop && classes.chipSelected)}
          label="Top"
          icon={<TopIcon />}
          size="small"
          variant={isTop ? 'default' : 'outlined'}
          onClick={() => setIsTop((previous) => !previous)}
        />
        <Chip
          className={clsx(classes.chip, isEco && classes.chipSelected)}
          label="Éco"
          icon={<EcoIcon />}
          size="small"
          variant={isEco ? 'default' : 'outlined'}
          onClick={() => setIsEco((previous) => !previous)}
        />
      </div>

      <div className={classes.topSection}>
        <div className={classes.imagePlaceholder} onClick={() => !isUploading && fileInputRef.current?.click()}>
          {isUploading ? (
            <CircularProgress size={40} />
          ) : imagePreview ? (
            <img src={imagePreview} alt="Aperçu" className={classes.imagePreview} />
          ) : (
            <AddAPhotoIcon className={classes.imageIcon} />
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          hidden
          onChange={handleImageChange}
        />

        <div className={classes.fieldsColumn}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Le nom est requis',
              validate: (value) => value.trim().length > 0 || 'Le nom est requis',
            }}
            render={({ field, fieldState }) => (
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                required
                label="Nom"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="reference"
            control={control}
            rules={{
              required: 'La référence est requise',
              validate: (value) => value.trim().length >= 2 || 'La référence doit contenir au moins 2 caractères',
            }}
            render={({ field, fieldState }) => (
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                required
                label="Référence"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{ required: 'La description est requise' }}
            render={({ field, fieldState }) => (
              <RichTextEditor
                value={field.value}
                onChange={field.onChange}
                placeholder="Description du panier"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </div>
      </div>

      <div className={classes.row}>
        <Controller
          name="category"
          control={control}
          rules={{ required: 'La rubrique est requise' }}
          render={({ field, fieldState }) => (
            <Autocomplete
              fullWidth
              size="small"
              value={field.value}
              options={[...categories].sort((first, second) => first.name.localeCompare(second.name))}
              groupBy={(option) => option.name[0].toUpperCase()}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, selected) => option.id === selected.id}
              onChange={(event, selectedOption: Category | null) => {
                field.onChange(selectedOption);
                setValue('subCategory', null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Rubrique"
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          name="subCategory"
          control={control}
          rules={{ required: 'La sous-rubrique est requise' }}
          render={({ field, fieldState }) => (
            <Autocomplete
              fullWidth
              size="small"
              value={field.value}
              disabled={!selectedCategory}
              options={[...subCategories].sort((first, second) => first.name.localeCompare(second.name))}
              groupBy={(option) => option.name[0].toUpperCase()}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, selected) => option.id === selected.id}
              onChange={(event, selectedOption: SubCategory | null) => field.onChange(selectedOption)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Sous-rubrique"
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </div>

      <div className={classes.row}>
        <Controller
          name="price"
          control={control}
          rules={{
            required: 'Le prix est requis',
            validate: (value) => Number(value) > 0 || 'Le prix doit être supérieur à 0',
          }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="number"
              label="Prix"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
              {...field}
            />
          )}
        />
        <Controller
          name="tax"
          control={control}
          rules={{ required: 'La TVA est requise' }}
          render={({ field, fieldState }) => (
            <Autocomplete
              fullWidth
              size="small"
              value={field.value}
              options={[...taxes].sort((first, second) => first.name.localeCompare(second.name))}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, selected) => option.id === selected.id}
              onChange={(event, selectedOption: Tax | null) => field.onChange(selectedOption)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="TVA"
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          name="size"
          control={control}
          render={({ field }) => (
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Taille</InputLabel>
              <Select
                native
                label="Taille"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value as BasketSize)}
              >
                {Object.values(BASKET_SIZES).map((basketSize) => (
                  <option key={basketSize} value={basketSize}>
                    {basketSize}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </div>

      <div className={classes.row}>
        <Controller
          name="weightFrom"
          control={control}
          rules={{
            required: 'Le poids min est requis',
            validate: (value) => Number(value) > 0 || 'Le poids min doit être supérieur à 0',
          }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="number"
              label="Poids min"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          name="weightTo"
          control={control}
          rules={{
            required: 'Le poids max est requis',
            validate: (value) =>
              Number(value) >= Number(getValues('weightFrom')) || 'Le poids max doit être ≥ au poids min',
          }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="number"
              label="Poids max"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          name="weightUnity"
          control={control}
          rules={{ required: "L'unité de poids est requise" }}
          render={({ field, fieldState }) => (
            <Autocomplete
              fullWidth
              size="small"
              value={field.value}
              options={[...weightUnities].sort((first, second) => first.name.localeCompare(second.name))}
              getOptionLabel={(option) => `${option.name} (${option.abbreviation})`}
              getOptionSelected={(option, selected) => option.id === selected.id}
              onChange={(event, selectedOption: WeightUnity | null) => field.onChange(selectedOption)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Unité de poids"
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </div>

      <div className={classes.row}>
        <Controller
          name="personsFrom"
          control={control}
          rules={{
            required: 'Le nombre de personnes min est requis',
            validate: (value) => Number(value) > 0 || 'Doit être supérieur à 0',
          }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="number"
              label="Personnes min"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          name="personsTo"
          control={control}
          rules={{
            required: 'Le nombre de personnes max est requis',
            validate: (value) =>
              Number(value) >= Number(getValues('personsFrom')) || 'Doit être ≥ au nombre de personnes min',
          }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="number"
              label="Personnes max"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
        <Controller
          name="stock"
          control={control}
          rules={{ validate: (value) => Number(value) >= 0 || 'Le stock ne peut pas être négatif' }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              type="number"
              label="Stock"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              {...field}
            />
          )}
        />
      </div>

      <div className={classes.row}>
        <Controller
          name="availabilityFrom"
          control={control}
          rules={{ required: 'La date de début est requise' }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="date"
              label="Disponible du"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              InputLabelProps={{ shrink: true }}
              {...field}
            />
          )}
        />
        <Controller
          name="availabilityTo"
          control={control}
          rules={{
            required: 'La date de fin est requise',
            validate: (value) =>
              !getValues('availabilityFrom') ||
              value >= getValues('availabilityFrom') ||
              'La date de fin doit être ≥ à la date de début',
          }}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              required
              type="date"
              label="Disponible au"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              InputLabelProps={{ shrink: true }}
              {...field}
            />
          )}
        />
      </div>

      <Controller
        name="tags"
        control={control}
        render={({ field }) => (
          <Autocomplete
            multiple
            fullWidth
            size="small"
            value={field.value}
            options={[...tags].sort((first, second) => first.name.localeCompare(second.name))}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, selected) => option.id === selected.id}
            onChange={(event, selectedOptions: Tag[]) => field.onChange(selectedOptions)}
            renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Tags" />}
          />
        )}
      />
    </div>
  );

  const handleProductCategoryFilterChange = (event: unknown, selectedOption: Category | null) => {
    setProductCategoryFilter(selectedOption);
    setProductSubCategoryFilter(null);
  };

  const renderStep2 = () => (
    <div className={classes.step2Container}>
      <div className={classes.step2Filters}>
        <TextField
          size="small"
          variant="outlined"
          label="Rechercher"
          value={productSearch}
          onChange={(event) => setProductSearch(event.target.value)}
        />
        <Autocomplete
          style={{ minWidth: 200 }}
          size="small"
          value={productCategoryFilter}
          options={[...categories].sort((first, second) => first.name.localeCompare(second.name))}
          groupBy={(option) => option.name[0].toUpperCase()}
          getOptionLabel={(option) => option.name}
          getOptionSelected={(option, selected) => option.id === selected.id}
          onChange={handleProductCategoryFilterChange}
          renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" />}
        />
        <Autocomplete
          style={{ minWidth: 200 }}
          size="small"
          value={productSubCategoryFilter}
          disabled={!productCategoryFilter}
          options={[...productSubCategories].sort((first, second) => first.name.localeCompare(second.name))}
          groupBy={(option) => option.name[0].toUpperCase()}
          getOptionLabel={(option) => option.name}
          getOptionSelected={(option, selected) => option.id === selected.id}
          onChange={(event, selectedOption: SubCategory | null) => setProductSubCategoryFilter(selectedOption)}
          renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Sous-rubrique" />}
        />
        <FormControl variant="outlined" size="small">
          <InputLabel>Statut</InputLabel>
          <Select
            native
            label="Statut"
            value={productIsActiveFilter}
            onChange={(event) => setProductIsActiveFilter(event.target.value as string)}
          >
            <option value="all">Tous</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </Select>
        </FormControl>
      </div>

      <TableContainer className={classes.productTableContainer}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 50 }} aria-label="Actions" />
              <TableCell style={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Référence</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Rubrique</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Sous-rubrique</TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="center">
                Actif
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableProducts.map((product) => (
              <TableRow key={product.id} hover>
                <TableCell>
                  <IconButton size="small" aria-label={`Ajouter ${product.name}`} onClick={() => addProduct(product)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.reference}</TableCell>
                <TableCell>{product.category.name}</TableCell>
                <TableCell>{product.subCategory.name}</TableCell>
                <TableCell align="center">
                  <StatusChip isActive={product.isActive} />
                </TableCell>
              </TableRow>
            ))}
            {availableProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  Aucun produit disponible
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className={classes.selectedSection}>
        <Typography className={classes.selectedHeader}>Produits sélectionnés ({selectedProducts.length})</Typography>
        <div className={classes.selectedList}>
          {selectedProducts.map((product) => (
            <Chip
              key={product.id}
              className={classes.selectedChip}
              label={product.name}
              size="small"
              onDelete={() => removeProduct(product.id)}
              deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
            />
          ))}
          {selectedProducts.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Aucun produit sélectionné
            </Typography>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className={classes.title}>Nouveau panier</DialogTitle>
      <Stepper activeStep={activeStep} className={classes.stepper}>
        <Step>
          <StepLabel>Informations</StepLabel>
        </Step>
        <Step>
          <StepLabel>Produits</StepLabel>
        </Step>
      </Stepper>
      <DialogContent>{activeStep === 0 ? renderStep1() : renderStep2()}</DialogContent>
      <DialogActions className={classes.actions}>
        {activeStep === 0 ? (
          <>
            <Button size="small" variant="text" onClick={onClose}>
              Annuler
            </Button>
            <Button size="small" variant="contained" className={classes.primaryButton} onClick={handleNextStep}>
              Suivant
            </Button>
          </>
        ) : (
          <>
            <Button size="small" variant="text" onClick={() => setActiveStep(0)}>
              Précédent
            </Button>
            <Button
              size="small"
              variant="contained"
              className={classes.primaryButton}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
              onClick={handleSubmit(onSubmit)}
            >
              Créer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CreateBasketDialog;
